import { useEffect, useRef, useState } from 'react';
import type { FieldMetadata, FieldValue } from '../../data/client';
import { FieldProvider } from '../../fields/FieldContext';
import { fieldShape } from '../../fields/fieldTypeMeta';
import { FieldInput } from '../../fields/input/FieldInput';
import { Button } from '../../ui';

/**
 * The open state of a field row. Field inputs deliberately do not own commit
 * semantics, so this is where they are decided for the record page:
 *
 *  - a single control (text, number, date) commits on Enter and on leaving the
 *    row, which is what a keyboard user expects from a form field;
 *  - everything composite (select, multi-select, relation, currency, switch)
 *    commits only on Save, because those controls open portals and toggle
 *    intermediate states - a blur commit there saves half an edit;
 *  - Escape always cancels, and Save/Cancel are always visible, because a phone
 *    has neither an Escape key nor a hover state.
 *
 * The draft lives here and this component only exists while the row is open, so
 * cancelling is a matter of unmounting rather than of resetting anything.
 */
export interface RecordFieldEditorProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly field: FieldMetadata;
  readonly value: FieldValue;
  readonly humanOwned: boolean;
  readonly onCommit: (next: FieldValue) => void;
  readonly onCancel: () => void;
}

/** Values are plain data (composites included), so structural equality is enough. */
function isSameValue(left: FieldValue, right: FieldValue): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function commitsOnBlur(field: FieldMetadata): boolean {
  const shape = fieldShape(field.type);
  return shape === 'text' || shape === 'number' || shape === 'date';
}

export function RecordFieldEditor({
  objectId,
  recordId,
  field,
  value,
  humanOwned,
  onCommit,
  onCancel,
}: RecordFieldEditorProps) {
  const [draft, setDraft] = useState<FieldValue>(value);
  const region = useRef<HTMLFieldSetElement>(null);
  const autoCommits = commitsOnBlur(field);

  useEffect(() => {
    // The row was activated on purpose, so the control is ready to be typed in.
    const control = region.current?.querySelector<HTMLElement>(
      'input, textarea, [role="combobox"], [role="switch"]',
    );
    control?.focus();
  }, []);

  const commit = () => {
    if (isSameValue(draft, value)) {
      // Nothing changed: no write, no event, no "saved" that means nothing.
      onCancel();
      return;
    }
    onCommit(draft);
  };

  return (
    <fieldset
      ref={region}
      // A real group element, not a bare div: the row holds several controls (a
      // composite input plus Save and Cancel) that belong to ONE field, and the
      // keyboard and focus handlers below need something with a role to hang on.
      // The name comes from aria-label rather than a <legend>, because a legend
      // does not take part in the grid this row is laid out with - and it names
      // the ACTION so the group and the control inside it stay distinguishable.
      aria-label={`Edit ${field.label}`}
      className="grid gap-2 px-4 py-3 md:grid-cols-[10rem_minmax(0,1fr)] md:gap-4"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
          return;
        }
        if (event.key === 'Enter' && autoCommits && !event.shiftKey) {
          event.preventDefault();
          commit();
        }
      }}
      onBlur={(event) => {
        if (!autoCommits) {
          return;
        }
        // Focus moving to the Save button is not leaving the row; only focus
        // landing outside it is.
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) {
          return;
        }
        commit();
      }}
    >
      <span className="text-xs font-medium text-ink-muted md:pt-2.5">{field.label}</span>

      <div className="min-w-0 space-y-2">
        <FieldProvider
          value={{
            objectId,
            recordId,
            field,
            value: draft,
            onChange: setDraft,
            readOnly: false,
            humanOwned,
            // The context carries no "refused by": a refusal names who HOLDS the
            // field, not which writer was turned away, and the UI does not know
            // its own actor. Surfacing it is this screen's job (see
            // useRecordFieldSave), not the field component's.
          }}
        >
          <FieldInput />
        </FieldProvider>

        {field.description !== undefined && (
          <p className="text-2xs text-ink-subtle">{field.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" onClick={commit}>
            Save
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          {autoCommits && (
            <span className="hidden text-2xs text-ink-subtle md:inline">
              Enter saves, Escape cancels
            </span>
          )}
        </div>
      </div>
    </fieldset>
  );
}
